import React, { useState, useEffect } from 'react';
import {
    Card, CardBody, Button, Chip, Spinner, Typography,
    Dialog, DialogHeader, DialogBody, DialogFooter,
} from '@material-tailwind/react';
import {
    RotateCcw, Plus, Filter, ChevronLeft, ChevronRight,
    Search, X, Trash2, RefreshCw,
} from 'lucide-react';
import moment from 'moment';
import {
    useGetRefunds, useAddRefund, useDeleteRefund,
} from '../../custom-hooks/use-refunds';
import { useGetBanks } from '../../custom-hooks/use-banks';
import { useGetUsers } from '../../custom-hooks/use-users';
import {
    REFUND_TYPES, REFUND_STATUSES, PAYMENT_METHODS, STATIC_USER_ROLES,
} from '../../utils/enums';
import { formatNumberWithCommas, Notify, convertToPresentableText } from '../../utils/functions';
import { useConfirm } from '../../custom-hooks/confirm-dialog';
import Autocomplete from '../../components/autocomplete';

const typeColors = {
    [REFUND_TYPES.SECURITY_DEPOSIT]: 'purple',
    [REFUND_TYPES.DAY_PASS]: 'cyan',
    [REFUND_TYPES.OTHER]: 'gray',
};

const statusColors = {
    [REFUND_STATUSES.PENDING]: 'amber',
    [REFUND_STATUSES.PROCESSED]: 'green',
    [REFUND_STATUSES.CANCELLED]: 'red',
};

const emptyForm = {
    type: REFUND_TYPES.SECURITY_DEPOSIT,
    customer: '',
    amount: '',
    paymentMethod: PAYMENT_METHODS.CASH,
    bank: '',
    refundDate: moment().format('YYYY-MM-DD'),
    reason: '',
    notes: '',
    status: REFUND_STATUSES.PROCESSED,
};

const RefundsPage = () => {
    const [filters, setFilters] = useState({ type: '', status: '', search: '', page: 1, limit: 20 });
    const [searchInput, setSearchInput] = useState('');
    const [refundDialog, setRefundDialog] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => setFilters(prev => ({ ...prev, search: searchInput, page: 1 })), 1000);
        return () => clearTimeout(t);
    }, [searchInput]);

    const { data, isLoading, refetch } = useGetRefunds(filters);
    const { data: banks = [] } = useGetBanks({ activeOnly: 'true' });
    // Fetch every customer once (role-filtered) and let the combobox filter client-side —
    // simpler and more reliably "all customers" than a live server search capped at a
    // small page size. 5000 comfortably covers realistic customer counts; revisit with
    // real pagination if that ever stops being true.
    const { data: customersData, isLoading: isLoadingCustomers } = useGetUsers({
        role: STATIC_USER_ROLES.USER,
        _limit: 5000,
    });
    const addMutation = useAddRefund();
    const deleteMutation = useDeleteRefund();
    const { confirm, ConfirmationDialog } = useConfirm();

    const refunds = data?.refunds || [];
    const pagination = data?.pagination || { total: 0, page: 1, totalPages: 1 };
    const customers = customersData?.users || [];

    const totalProcessed = refunds
        .filter(r => r.status === REFUND_STATUSES.PROCESSED)
        .reduce((sum, r) => sum + (r.amount || 0), 0);

    const openCreate = () => {
        setForm(emptyForm);
        setSelectedCustomerOption(null);
        setRefundDialog(true);
    };

    const handleCreate = async () => {
        if (!form.customer) {
            Notify('Please select a customer', 'error');
            return;
        }
        const amt = parseFloat(form.amount);
        if (!amt || amt <= 0) {
            Notify('Enter a valid refund amount', 'error');
            return;
        }
        if (form.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && !form.bank) {
            Notify('Please select a bank for bank transfer', 'error');
            return;
        }

        try {
            await addMutation.mutateAsync({
                type: form.type,
                customer: form.customer,
                amount: amt,
                paymentMethod: form.paymentMethod,
                bank: form.bank || undefined,
                refundDate: form.refundDate,
                reason: form.reason,
                notes: form.notes,
                status: form.status,
            });
            Notify('Refund recorded successfully', 'success');
            setRefundDialog(false);
            setForm(emptyForm);
            refetch();
        } catch (error) {
            Notify(error?.response?.data?.message || 'Failed to record refund', 'error');
        }
    };

    const handleDelete = async (refund) => {
        const isConfirmed = await confirm({
            title: 'Delete Refund',
            message: `Delete this refund of PKR ${formatNumberWithCommas(refund.amount)}? This cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            confirmBtnColor: 'red',
        });
        if (!isConfirmed) return;
        try {
            await deleteMutation.mutateAsync(refund._id);
            Notify('Refund deleted', 'success');
            refetch();
        } catch (error) {
            Notify('Failed to delete refund', 'error');
        }
    };

    return (
        <div className='p-4 md:p-6'>
            {/* Header */}
            <div className='flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4'>
                <div>
                    <div className='text-2xl font-thin'>Refunds</div>
                    {refunds.length > 0 && (
                        <div className='text-xs text-gray-500 mt-1'>
                            Total processed (this page): <span className='font-medium text-green-600'>PKR {formatNumberWithCommas(totalProcessed)}</span>
                        </div>
                    )}
                </div>
                <div className='flex flex-wrap gap-2'>
                    <Button
                        variant='gradient' color='gray' size='sm'
                        className='flex items-center gap-2 rounded-full'
                        onClick={openCreate}
                    >
                        <Plus className='h-4 w-4' /> Refund
                    </Button>
                    <Button
                        variant='outlined' color='gray' size='sm'
                        className='flex items-center gap-2 rounded-full'
                        onClick={refetch}
                    >
                        <RefreshCw className='h-4 w-4' /> Refresh
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className='flex flex-wrap gap-3 mb-6 items-center'>
                <div className='relative flex-1 min-w-[200px] max-w-xs'>
                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                        type='text'
                        placeholder='Search customer or reason...'
                        className='w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                    {searchInput && (
                        <button
                            className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                            onClick={() => { setSearchInput(''); setFilters(prev => ({ ...prev, search: '', page: 1 })); }}
                        >
                            <X className='h-4 w-4' />
                        </button>
                    )}
                </div>

                <div className='flex items-center gap-2 text-gray-500'>
                    <Filter className='h-4 w-4' />
                    <span className='text-sm'>Filter:</span>
                </div>

                <select
                    className='border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                    value={filters.type}
                    onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value, page: 1 }))}
                >
                    <option value=''>All Types</option>
                    {Object.values(REFUND_TYPES).map(t => (
                        <option key={t} value={t}>{convertToPresentableText(t)}</option>
                    ))}
                </select>

                <select
                    className='border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                >
                    <option value=''>All Statuses</option>
                    {Object.values(REFUND_STATUSES).map(s => (
                        <option key={s} value={s}>{convertToPresentableText(s)}</option>
                    ))}
                </select>
            </div>

            {/* Table / cards */}
            {isLoading ? (
                <div className='flex justify-center items-center min-h-[30vh]'>
                    <Spinner />
                </div>
            ) : refunds.length === 0 ? (
                <div className='flex flex-col items-center justify-center py-16 bg-gray-50 rounded-lg'>
                    <RotateCcw className='h-12 w-12 text-gray-400 mb-4' />
                    <Typography variant='h6' color='gray'>No refunds yet</Typography>
                    <Typography variant='small' className='text-gray-500 mt-2 text-center max-w-md'>
                        Use the Refund button to record a refund for a security deposit, day pass, or other reason.
                    </Typography>
                </div>
            ) : (
                <>
                    <div className='hidden md:block overflow-x-auto'>
                        <table className='w-full min-w-[800px] text-left'>
                            <thead>
                                <tr className='border-b border-gray-200'>
                                    <th className='py-3 px-4 text-xs font-semibold text-gray-600 uppercase'>Date</th>
                                    <th className='py-3 px-4 text-xs font-semibold text-gray-600 uppercase'>Customer</th>
                                    <th className='py-3 px-4 text-xs font-semibold text-gray-600 uppercase'>Type</th>
                                    <th className='py-3 px-4 text-xs font-semibold text-gray-600 uppercase text-right'>Amount</th>
                                    <th className='py-3 px-4 text-xs font-semibold text-gray-600 uppercase'>Method</th>
                                    <th className='py-3 px-4 text-xs font-semibold text-gray-600 uppercase'>Reason</th>
                                    <th className='py-3 px-4 text-xs font-semibold text-gray-600 uppercase'>Status</th>
                                    <th className='py-3 px-4 text-xs font-semibold text-gray-600 uppercase text-right'>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {refunds.map((r) => (
                                    <tr key={r._id} className='border-b border-gray-100 hover:bg-gray-50 transition-colors'>
                                        <td className='py-3 px-4 text-sm'>{moment(r.refundDate).format('MMM DD, YYYY')}</td>
                                        <td className='py-3 px-4 text-sm'>
                                            <div className='font-medium'>{r.customer?.name || 'N/A'}</div>
                                            <div className='text-xs text-gray-500'>{r.customer?.email}</div>
                                        </td>
                                        <td className='py-3 px-4'>
                                            <Chip
                                                value={convertToPresentableText(r.type)}
                                                color={typeColors[r.type] || 'gray'}
                                                size='sm'
                                                variant='ghost'
                                                className='text-[10px] uppercase font-bold rounded-full text-center'
                                            />
                                        </td>
                                        <td className='py-3 px-4 text-sm text-right font-medium text-red-600'>
                                            PKR {formatNumberWithCommas(r.amount)}
                                        </td>
                                        <td className='py-3 px-4 text-sm'>
                                            <div>{r.paymentMethod ? convertToPresentableText(r.paymentMethod) : '-'}</div>
                                            {r.bank?.name && (
                                                <div className='text-[10px] text-gray-500'>{r.bank.name}</div>
                                            )}
                                        </td>
                                        <td className='py-3 px-4 text-sm text-gray-600 max-w-[200px] truncate'>{r.reason || '-'}</td>
                                        <td className='py-3 px-4'>
                                            <Chip
                                                value={convertToPresentableText(r.status)}
                                                color={statusColors[r.status] || 'gray'}
                                                size='sm'
                                                variant='ghost'
                                                className='text-[10px] uppercase font-bold rounded-full text-center'
                                            />
                                        </td>
                                        <td className='py-3 px-4'>
                                            <div className='flex items-center justify-end gap-1'>
                                                <button
                                                    className='p-1.5 hover:bg-red-50 rounded-lg transition-colors'
                                                    onClick={() => handleDelete(r)}
                                                    title='Delete'
                                                >
                                                    <Trash2 className='h-4 w-4 text-red-600' />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile */}
                    <div className='md:hidden space-y-3'>
                        {refunds.map((r) => (
                            <Card key={r._id} className='shadow-none border border-gray-200'>
                                <CardBody className='p-4'>
                                    <div className='flex justify-between items-start mb-2'>
                                        <div>
                                            <div className='text-sm font-medium'>{r.customer?.name || 'N/A'}</div>
                                            <div className='text-xs text-gray-500'>{moment(r.refundDate).format('MMM DD, YYYY')}</div>
                                        </div>
                                        <Chip
                                            value={convertToPresentableText(r.type)}
                                            color={typeColors[r.type] || 'gray'}
                                            size='sm' variant='ghost'
                                            className='text-[10px] uppercase font-bold rounded-full'
                                        />
                                    </div>
                                    <div className='flex justify-between items-center mb-2'>
                                        <span className='text-sm text-gray-500'>{r.paymentMethod ? convertToPresentableText(r.paymentMethod) : '-'}</span>
                                        <span className='text-base font-semibold text-red-600'>PKR {formatNumberWithCommas(r.amount)}</span>
                                    </div>
                                    {r.reason && <div className='text-xs text-gray-600 mb-2'>{r.reason}</div>}
                                    <div className='flex justify-between items-center'>
                                        <Chip
                                            value={convertToPresentableText(r.status)}
                                            color={statusColors[r.status] || 'gray'}
                                            size='sm' variant='ghost'
                                            className='text-[10px] uppercase font-bold rounded-full'
                                        />
                                        <button
                                            className='p-1.5 hover:bg-red-50 rounded-lg transition-colors'
                                            onClick={() => handleDelete(r)}
                                            title='Delete'
                                        >
                                            <Trash2 className='h-4 w-4 text-red-600' />
                                        </button>
                                    </div>
                                </CardBody>
                            </Card>
                        ))}
                    </div>

                    {pagination.totalPages > 1 && (
                        <div className='flex items-center justify-between mt-6'>
                            <Typography variant='small' className='text-gray-500'>
                                Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                            </Typography>
                            <div className='flex gap-2'>
                                <Button
                                    size='sm' variant='outlined' color='gray'
                                    disabled={pagination.page <= 1}
                                    onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                >
                                    <ChevronLeft className='h-4 w-4' />
                                </Button>
                                <Button
                                    size='sm' variant='outlined' color='gray'
                                    disabled={pagination.page >= pagination.totalPages}
                                    onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                >
                                    <ChevronRight className='h-4 w-4' />
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Create Refund Dialog */}
            <Dialog open={refundDialog} handler={() => setRefundDialog(false)} size='sm'>
                <DialogHeader>Record Refund</DialogHeader>
                <DialogBody className='space-y-4 max-h-[70vh] overflow-y-auto'>
                    <div>
                        <label className='text-sm text-gray-600 mb-1 block'>Refund Type *</label>
                        <select
                            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
                            value={form.type}
                            onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
                        >
                            {Object.values(REFUND_TYPES).map(t => (
                                <option key={t} value={t}>{convertToPresentableText(t)}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className='text-sm text-gray-600 mb-1 block'>Customer *</label>
                        <Autocomplete
                            placeholder='Search customer by name or email...'
                            isLoading={isLoadingCustomers}
                            isClearable
                            options={customers.map(c => ({
                                value: c._id,
                                label: `${c.name}${c.email ? ` (${c.email})` : ''}`,
                            }))}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.value}
                            value={selectedCustomerOption}
                            onChange={(option) => {
                                setSelectedCustomerOption(option);
                                setForm(prev => ({ ...prev, customer: option?.value || '' }));
                            }}
                        />
                    </div>

                    <div className='grid grid-cols-2 gap-3'>
                        <div>
                            <label className='text-sm text-gray-600 mb-1 block'>Amount (PKR) *</label>
                            <input
                                type='number'
                                className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                                value={form.amount}
                                onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label className='text-sm text-gray-600 mb-1 block'>Refund Date</label>
                            <input
                                type='date'
                                className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                                value={form.refundDate}
                                onChange={(e) => setForm(prev => ({ ...prev, refundDate: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div>
                        <label className='text-sm text-gray-600 mb-1 block'>Payment Method</label>
                        <select
                            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
                            value={form.paymentMethod}
                            onChange={(e) => setForm(prev => ({ ...prev, paymentMethod: e.target.value, bank: '' }))}
                        >
                            {Object.values(PAYMENT_METHODS)
                                .filter(m => m !== PAYMENT_METHODS.SECURITY_ADJUSTMENT)
                                .map(m => (
                                    <option key={m} value={m}>{convertToPresentableText(m)}</option>
                                ))}
                        </select>
                    </div>

                    {form.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && (
                        <div>
                            <label className='text-sm text-gray-600 mb-1 block'>Bank *</label>
                            <select
                                className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
                                value={form.bank}
                                onChange={(e) => setForm(prev => ({ ...prev, bank: e.target.value }))}
                            >
                                <option value=''>Select bank</option>
                                {banks.map(b => (
                                    <option key={b._id} value={b._id}>
                                        {b.name}{b.accountTitle ? ` — ${b.accountTitle}` : ''}
                                    </option>
                                ))}
                            </select>
                            {banks.length === 0 && (
                                <p className='text-[10px] text-amber-600 mt-1'>
                                    No banks configured. Add one in Settings → Payments.
                                </p>
                            )}
                        </div>
                    )}

                    <div>
                        <label className='text-sm text-gray-600 mb-1 block'>Status</label>
                        <select
                            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
                            value={form.status}
                            onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
                        >
                            {Object.values(REFUND_STATUSES).map(s => (
                                <option key={s} value={s}>{convertToPresentableText(s)}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className='text-sm text-gray-600 mb-1 block'>Reason</label>
                        <input
                            type='text'
                            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                            placeholder='e.g. Day pass cancelled, security deposit return'
                            value={form.reason}
                            onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value }))}
                        />
                    </div>

                    <div>
                        <label className='text-sm text-gray-600 mb-1 block'>Notes</label>
                        <textarea
                            rows={2}
                            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                            value={form.notes}
                            onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                        />
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button variant='text' onClick={() => setRefundDialog(false)} className='mr-2'>Cancel</Button>
                    <Button
                        variant='gradient' color='gray'
                        onClick={handleCreate}
                        disabled={addMutation.isLoading}
                        className='flex items-center gap-2'
                    >
                        {addMutation.isLoading ? <Spinner className='h-4 w-4' /> : <RotateCcw className='h-4 w-4' />}
                        Record Refund
                    </Button>
                </DialogFooter>
            </Dialog>
            <ConfirmationDialog />
        </div>
    );
};

export default RefundsPage;
